import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Star, CheckCircle2, Loader2, MapPin } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

export default function FeedbackPage() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const [ctx, setCtx] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [rating, setRating] = useState(() => {
        const r = parseInt(searchParams.get("rating") || "0", 10);
        return r >= 1 && r <= 5 ? r : 0;
    });
    const [hoverRating, setHoverRating] = useState(0);
    const [nps, setNps] = useState(null);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/feedback/${token}`);
                setCtx(data);
                if (data.already_submitted) setDone(true);
            } catch {
                setNotFound(true);
            }
        })();
    }, [token]);

    const submit = async () => {
        if (rating < 1) {
            toast.error("Seleziona almeno una stella");
            return;
        }
        if (nps == null) {
            toast.error("Seleziona quanto consiglieresti Magniflex");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/feedback/${token}`, { rating, nps, comment });
            setDone(true);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (notFound) {
        return (
            <div className="min-h-screen flex flex-col bg-magni-cream">
                <SiteHeader />
                <main className="flex-1 flex items-center justify-center px-4 py-20 text-center">
                    <div>
                        <div className="overline mb-3">Link non valido</div>
                        <h1 className="font-serif-display text-3xl text-magni-navy mb-3">
                            Questa pagina non è disponibile
                        </h1>
                        <p className="text-magni-muted">
                            Il link potrebbe essere scaduto o non corretto.
                        </p>
                    </div>
                </main>
                <SiteFooter />
            </div>
        );
    }

    if (!ctx) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-magni-cream">
                <Loader2 className="w-8 h-8 animate-spin text-magni-navy" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-magni-cream">
            <SiteHeader />
            <Toaster position="top-center" richColors />
            <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-12">
                {done ? (
                    <div className="bg-white border border-magni-light p-10 sm:p-14 text-center magni-fade">
                        <div className="w-16 h-16 mx-auto bg-magni-navy text-white flex items-center justify-center mb-6">
                            <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <div className="overline mb-3">Grazie!</div>
                        <h1 className="font-serif-display text-3xl sm:text-4xl text-magni-navy mb-4">
                            Il tuo feedback è prezioso
                        </h1>
                        <p className="text-magni-muted">
                            Grazie per averci raccontato la tua esperienza. Ci aiuti a migliorare
                            ogni giorno.
                        </p>
                    </div>
                ) : (
                    <div className="bg-white border border-magni-light p-6 sm:p-10 magni-fade">
                        <div className="overline mb-2">Feedback prova MagniStretch®</div>
                        <h1 className="font-serif-display text-3xl sm:text-4xl text-magni-navy mb-3">
                            Com'è andata, {ctx.first_name}?
                        </h1>
                        <div className="text-sm text-magni-muted mb-8 flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {ctx.store?.name} — {ctx.store?.city}
                        </div>

                        <div className="mb-8">
                            <div className="overline mb-3">La tua valutazione</div>
                            <div className="flex items-center gap-2" data-testid="rating-stars">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        onClick={() => setRating(n)}
                                        onMouseEnter={() => setHoverRating(n)}
                                        onMouseLeave={() => setHoverRating(0)}
                                        data-testid={`star-${n}`}
                                        className="p-1 transition-transform hover:scale-110"
                                        aria-label={`${n} stelle`}
                                    >
                                        <Star
                                            className={`w-10 h-10 ${
                                                (hoverRating || rating) >= n
                                                    ? "fill-magni-navy text-magni-navy"
                                                    : "text-magni-light"
                                            }`}
                                        />
                                    </button>
                                ))}
                                <span className="ml-3 text-sm text-magni-muted">
                                    {rating > 0 && `${rating}/5`}
                                </span>
                            </div>
                        </div>

                        <div className="mb-8">
                            <div className="overline mb-2">
                                Quanto consiglieresti Magniflex a un amico?
                            </div>
                            <div className="text-xs text-magni-muted mb-3">
                                Da 1 (per niente) a 10 (assolutamente sì)
                            </div>
                            <div
                                className="grid grid-cols-5 sm:grid-cols-10 gap-2"
                                data-testid="nps-buttons"
                            >
                                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                    <button
                                        key={n}
                                        onClick={() => setNps(n)}
                                        data-testid={`nps-${n}`}
                                        className={`py-3 text-sm font-semibold transition-all border ${
                                            nps === n
                                                ? "bg-magni-navy text-white border-magni-navy"
                                                : "bg-white border-magni-light text-magni-navy hover:border-magni-navy"
                                        }`}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mb-8">
                            <label className="overline mb-2 block">
                                Vuoi aggiungere un commento? (facoltativo)
                            </label>
                            <textarea
                                rows={4}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Cosa ti è piaciuto di più? Cosa possiamo migliorare?"
                                className="magni-input"
                                data-testid="feedback-comment"
                            />
                        </div>

                        <button
                            onClick={submit}
                            disabled={submitting}
                            data-testid="submit-feedback-btn"
                            className="magni-btn-primary w-full sm:w-auto"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...
                                </>
                            ) : (
                                "Invia la mia valutazione"
                            )}
                        </button>
                    </div>
                )}
            </main>
            <SiteFooter />
        </div>
    );
}
